// ==============================
// 파일명 : src/components/users/UserFormModal.jsx
// 역할 : 유저 추가/수정 모달 전반의 입력 상태, UID 복사 안내, 위치 보정, 저장 로직을 통합 관리하고
//         Firebase Auth UID 자동 생성 및 정규화, 팩 선택, 비밀번호 기본값 처리까지 아우르는 UI 컨테이너 역할 수행
// 수정일 : 2025-10-14
// ==============================

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PackSelector from "./PackSelector";
import { USER_FORM_MODAL_TEXT, USER_FORM_DEFAULT_PASSWORD } from "../../constants/users/form"; // [수정] 모달 문구와 기본 상수
import { USER_FIELD_AUTH_UID } from "../../constants/users/fields"; // [수정] 인증 UID 필드 이름 상수
import { USER_FORM_VALIDATION_MESSAGES } from "../../constants/users/validation"; // [수정] 이메일 입력 검증 안내 문구
import { clampValue } from "../../utils/math"; // [수정] 위치 보정을 위한 공용 헬퍼
import { normalizeAuthUid } from "../../utils/users"; // [수정] UID 정규화 유틸
import { generateUserAuthUid } from "../../utils/uid"; // [수정] UID 자동 생성 유틸
import { isEmailFormatValid } from "../../utils/validation/email"; // [수정] 이메일 형식 검증 유틸

const UID_COPY_FEEDBACK_DURATION = 2000; // [수정] UID 복사 안내 유지 시간(ms)

export default function UserFormModal({
  mode = "add",
  initialUser = null,
  packs = [],
  anchorRect = null,
  onClose = () => {},
  onSave = () => {},
  yOffset = 8,
}) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false); // [수정] 이메일 입력이 상호작용되었는지 추적
  const [emailError, setEmailError] = useState(""); // [수정] 이메일 오류 문구 상태
  const [owned, setOwned] = useState([]); // packId[]
  const [password, setPassword] = useState(USER_FORM_DEFAULT_PASSWORD); // [수정] 기본 비밀번호 적용
  const [authUid, setAuthUid] = useState(() => generateUserAuthUid()); // [수정] Firebase UID 자동 생성 상태
  const [uidCopyMessage, setUidCopyMessage] = useState(""); // [수정] UID 복사 안내 문구

  const modalRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState({});
  const copyTimeoutRef = useRef(null); // [수정] UID 복사 안내 타이머 저장
  const overlayMouseDownRef = useRef(false); // [수정] 오버레이에서 마우스다운 여부 추적

  const margin = 16; // 여백을 두어 화면에 가리지 않도록 함

  const clearCopyFeedback = useCallback(() => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current); // [수정] 이전 타이머 정리
      copyTimeoutRef.current = null; // [수정] 참조 초기화
    }
    setUidCopyMessage(""); // [수정] 안내 문구 초기화
  }, []); // [수정] UID 복사 안내 초기화 함수

  const showCopyFeedback = useCallback(
    (message) => {
      clearCopyFeedback(); // [수정] 기존 안내 초기화
      if (!message) return; // [수정] 빈 메시지는 표시하지 않음
      setUidCopyMessage(message); // [수정] 새 안내 문구 표시
      copyTimeoutRef.current = setTimeout(() => {
        setUidCopyMessage(""); // [수정] 시간 경과 후 안내 제거
        copyTimeoutRef.current = null; // [수정] 타이머 참조 초기화
      }, UID_COPY_FEEDBACK_DURATION);
    },
    [clearCopyFeedback]
  ); // [수정] UID 복사 안내 표시 함수

  useEffect(() => {
    if (mode === "edit" && initialUser) {
      setDisplayName(initialUser.displayName || initialUser.name || "");
      setEmail(initialUser.email || "");
      setEmailTouched(false); // [수정] 초기 로딩 시 터치 상태 초기화
      setEmailError(""); // [수정] 초기 로딩 시 오류 문구 초기화
      const ids =
        initialUser.ownedPacks ||
        initialUser.ownedPackIds ||
        initialUser.packs ||
        initialUser.purchased_packs ||
        [];
      setOwned(Array.isArray(ids) ? ids : []);
      setPassword(""); // 수정 시에는 비밀번호를 초기화 (입력 시에만 변경)
      setAuthUid(normalizeAuthUid(initialUser[USER_FIELD_AUTH_UID], initialUser.id)); // [수정] 기존 UID 불러오기
    } else {
      setDisplayName("");
      setEmail("");
      setEmailTouched(false); // [수정] 추가 모드 초기화 시 터치 상태 초기화
      setEmailError(""); // [수정] 추가 모드 초기화 시 오류 문구 초기화
      setOwned([]);
      setPassword(USER_FORM_DEFAULT_PASSWORD); // [수정] 기본 비밀번호로 초기화
      setAuthUid(generateUserAuthUid()); // [수정] 추가 모드에서 UID 자동 생성
    }
  clearCopyFeedback(); // [수정] 모드 전환 시 안내 초기화
  }, [mode, initialUser, clearCopyFeedback]);

  const trimmedEmail = email.trim(); // [수정] 공백을 제거한 이메일 값

  useEffect(() => {
    if (!emailTouched) {
      return; // [수정] 입력이 시작되기 전에는 오류 표시를 건너뜀
    }
    if (!trimmedEmail) {
      setEmailError(USER_FORM_VALIDATION_MESSAGES.emailRequired); // [수정] 미입력 오류 메시지 적용
      return;
    }
    if (!isEmailFormatValid(trimmedEmail)) {
      setEmailError(USER_FORM_VALIDATION_MESSAGES.emailInvalid); // [수정] 형식 오류 메시지 적용
      return;
    }
    setEmailError(""); // [수정] 모든 조건 통과 시 오류 해제
  }, [emailTouched, trimmedEmail]);

  useEffect(() => {
    return () => {
      clearCopyFeedback(); // [수정] 언마운트 시 안내 타이머 정리
    };
  }, [clearCopyFeedback]);

  useLayoutEffect(() => {
   const updatePosition = () => { // [수정] 뷰포트 크기에 맞춘 위치 계산 함수
      if (!modalRef.current) return; // [수정] 레퍼런스 확인

      const viewportWidth = window.innerWidth; // [수정] 현재 화면 너비
      const viewportHeight = window.innerHeight; // [수정] 현재 화면 높이
      const modalWidth = Math.min(720, viewportWidth - margin * 2); // [수정] 최대 너비 제한
      const modalHeight = modalRef.current.offsetHeight || 520; // [수정] 모달 높이 측정

      const baseLeft = anchorRect
        ? anchorRect.left // [수정] 버튼 기준 좌표 사용
        : (viewportWidth - modalWidth) / 2; // [수정] 중앙 정렬 기본값
      const baseTop = anchorRect
        ? anchorRect.bottom + (yOffset || 0) // [수정] 버튼 아래 배치
        : (viewportHeight - modalHeight) / 2; // [수정] 중앙 정렬 기본값

      const maxLeft = Math.max(margin, viewportWidth - margin - modalWidth); // [수정] 좌측 상한 계산
      const maxTop = Math.max(margin, viewportHeight - margin - modalHeight); // [수정] 상단 상한 계산

      const safeLeft = clampValue(baseLeft ?? margin, margin, maxLeft); // [수정] 안전한 X 좌표
      const safeTop = clampValue(baseTop ?? margin, margin, maxTop); // [수정] 안전한 Y 좌표

      setPositionStyle({
        position: "fixed",
        left: safeLeft, // [수정] 보정된 X 좌표 적용
        top: safeTop, // [수정] 보정된 Y 좌표 적용
        width: modalWidth,
        maxHeight: `calc(100vh - ${margin * 2}px)`,
        overflowY: "auto",
        zIndex: 50,
      });
    };
  
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [anchorRect, yOffset, mode, displayName, email, owned, password, authUid]); // [수정] UID 변경 시 위치 재계산

  const handleSave = () => {
    console.log("🔥🔥🔥 handleSave 시점의 owned:", owned);  // ← 이렇게 수정

    const nextEmailTouched = true;
    setEmailTouched(nextEmailTouched); // [수정] 저장 시 터치 상태 강제 활성화
    const sanitizedEmail = trimmedEmail; // [수정] 검증 대상 이메일 캐싱
    if (!sanitizedEmail) {
      setEmailError(USER_FORM_VALIDATION_MESSAGES.emailRequired); // [수정] 이메일 미입력 방지
      return;
    }
    if (!isEmailFormatValid(sanitizedEmail)) {
      setEmailError(USER_FORM_VALIDATION_MESSAGES.emailInvalid); // [수정] 이메일 형식 오류 방지
      return;
    }
    const payload = {
      displayName: displayName.trim(),
      email: sanitizedEmail,
      ownedPacks: owned, // 표준 필드로 유지
    };
    console.log("🔥🔥🔥 payload.ownedPacks:", payload.ownedPacks);
    const trimmedPassword = password.trim();
    if (mode === "add") {
       payload.password = trimmedPassword || USER_FORM_DEFAULT_PASSWORD; // [수정] 기본 비밀번호 보정
    } else if (trimmedPassword) {
      payload.password = trimmedPassword;
    }
    payload[USER_FIELD_AUTH_UID] = normalizeAuthUid(authUid, initialUser?.id); // [수정] UID를 정규화하여 전달
    onSave(mode, payload, initialUser?.id);
  };

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault(); // [수정] 기본 제출 동작 방지
      handleSave(); // [수정] 저장 로직 실행
    },
    [handleSave]
  ); // [수정] 폼 제출 핸들러

  const handleCopyUid = useCallback(async () => {
    if (!authUid) {
      showCopyFeedback(""); // [수정] UID가 없으면 안내 리셋
      return;
    }
    try {
      await navigator.clipboard.writeText(authUid); // [수정] UID를 클립보드에 기록
      showCopyFeedback(USER_FORM_MODAL_TEXT.feedback.uidCopied); // [수정] 성공 안내 표시
    } catch (error) {
      console.error("log :", "copy uid failed", error); // [수정] 복사 실패 로그 출력
      showCopyFeedback(USER_FORM_MODAL_TEXT.feedback.uidCopyFailed); // [수정] 실패 안내 표시
    }
  }, [authUid, showCopyFeedback]); // [수정] UID 복사 핸들러

  const passwordPlaceholder =
    mode === "add"
      ? `${USER_FORM_MODAL_TEXT.placeholders.passwordAddPrefix}${USER_FORM_DEFAULT_PASSWORD}`
      : USER_FORM_MODAL_TEXT.placeholders.passwordEdit; // [수정] 상황에 따라 달라지는 비밀번호 안내 문구

      const handleOverlayMouseDown = useCallback((event) => {
    if (event.target === event.currentTarget) {
      overlayMouseDownRef.current = true; // [수정] 오버레이에서 시작된 누름 상태 기록
    } else {
      overlayMouseDownRef.current = false; // [수정] 자식 요소에서 시작 시 초기화
    }
  }, []); // [수정] 오버레이 마우스다운 핸들러

  const handleOverlayMouseUp = useCallback(
    (event) => {
      const startedOnOverlay = overlayMouseDownRef.current; // [수정] 시작 위치 확인
      overlayMouseDownRef.current = false; // [수정] 사용 후 상태 초기화
      if (startedOnOverlay && event.target === event.currentTarget) {
        onClose(); // [수정] 클릭이 온전히 오버레이에서 끝난 경우에만 닫기
      }
    },
    [onClose]
  ); // [수정] 오버레이 마우스업 핸들러

  return (
    <div
      className="fixed inset-0 bg-black/30 z-40"
      onMouseDown={handleOverlayMouseDown} // [수정] 드래그 시작 위치 추적
      onMouseUp={handleOverlayMouseUp} // [수정] 오버레이 클릭 조건부 처리
    >
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-xl p-5 space-y-4"
        style={positionStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">{mode === "add" ? USER_FORM_MODAL_TEXT.titles.add : USER_FORM_MODAL_TEXT.titles.edit}</div> {/* [수정] 텍스트 상수 적용 */}
          <button className="text-gray-500 hover:text-black" onClick={onClose}>✕</button>
        </div>

        {/* 폼 */}
        <form className="space-y-3" onSubmit={handleSubmit}> {/* [수정] Enter 키로 저장 지원 */}
          <div>
            <div className="text-sm text-gray-600 mb-1">{USER_FORM_MODAL_TEXT.labels.name}</div> {/* [수정] 라벨 상수화 */}
            <input
              className="border rounded px-3 py-2 w-full"
              placeholder={USER_FORM_MODAL_TEXT.placeholders.name}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">{USER_FORM_MODAL_TEXT.labels.email}</div> {/* [수정] 라벨 상수화 */}
            <input
              className={`border rounded px-3 py-2 w-full ${emailError ? "border-red-500" : ""}`}
              placeholder={USER_FORM_MODAL_TEXT.placeholders.email}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value); // [수정] 이메일 입력값 갱신
                if (!emailTouched) {
                  setEmailTouched(true); // [수정] 최초 입력 시 터치 상태 갱신
                }
              }}
              onBlur={() => setEmailTouched(true)} // [수정] 포커스 해제 시에도 터치 상태 유지
            />
            {emailError && (
              <p className="mt-1 text-xs text-red-600">{emailError}</p>
            )} {/* [수정] 이메일 오류 안내 문구 */}
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1 flex items-center justify-between">
              <span>{USER_FORM_MODAL_TEXT.labels.authUid}</span> {/* [수정] UID 라벨 상수화 */}
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={handleCopyUid}
              >
                {USER_FORM_MODAL_TEXT.actions.copyUid}
              </button> {/* [수정] UID 복사 버튼 */}
            </div>
            <input
              className="border rounded px-3 py-2 w-full bg-gray-100" /* [수정] UID 직접 입력 차단 */
              placeholder={USER_FORM_MODAL_TEXT.placeholders.authUid}
              value={authUid}
              readOnly
            />
            <p className="mt-1 text-xs text-gray-500">
              {mode === "add"
                ? USER_FORM_MODAL_TEXT.guides.authUidAdd
                : USER_FORM_MODAL_TEXT.guides.authUidEdit}
            </p> {/* [수정] UID 안내 문구 */}
            {uidCopyMessage && (
              <p className="mt-1 text-xs text-blue-600">{uidCopyMessage}</p>
            )} {/* [수정] UID 복사 안내 표시 */}
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-1">{USER_FORM_MODAL_TEXT.labels.password}</div> {/* [수정] 라벨 상수화 */}
            <input
              type="password"
              className="border rounded px-3 py-2 w-full"
              placeholder={passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {mode === "edit" && (
              <p className="mt-1 text-xs text-gray-500">
                {USER_FORM_MODAL_TEXT.guides.passwordEdit}
              </p>
            )} {/* [수정] 안내 문구 상수화 */}
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-2">{USER_FORM_MODAL_TEXT.labels.packs}</div> {/* [수정] 라벨 상수화 */}
            <PackSelector packs={packs} value={owned} onChange={setOwned} height={260} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose}>
              {USER_FORM_MODAL_TEXT.actions.cancel}
            </button> {/* [수정] 버튼 텍스트 상수화 */}
            <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
              {USER_FORM_MODAL_TEXT.actions.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
